import React, { useEffect, useState } from 'react';
import { collection, getDoc, onSnapshot, query, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { formatarMoeda as formatCurrency } from '../../../utils/currencyUtils';
import { db } from '@/config/firebase.config';
import { Cobranca, compartilharCobranca } from './CobrancaModal';

interface CobrancasAbertasProps {
  unidadeId: string;
  onVer: (cobrancaId: string) => void;
  /** Uma cobrança saiu da lista: foi paga ou cancelada. */
  onEncerrada: (cobranca: Cobranca) => void;
}

const minutosDesde = (cobranca: Cobranca) => {
  const criada = cobranca.criadaEm?.toDate();
  if (!criada) return 'agora';
  const minutos = Math.floor((Date.now() - criada.getTime()) / 60000);
  if (minutos < 1) return 'agora';
  if (minutos < 60) return `há ${minutos} min`;
  return `há ${Math.floor(minutos / 60)} h ${minutos % 60} min`;
};

const CobrancasAbertas: React.FC<CobrancasAbertasProps> = ({ unidadeId, onVer, onEncerrada }) => {
  const [cobrancas, setCobrancas] = useState<Cobranca[]>([]);
  const [cancelando, setCancelando] = useState<string | null>(null);
  const [, setRelogio] = useState(0);

  useEffect(() => {
    const abertas = query(
      collection(db, 'cobrancas'),
      where('unidadeId', '==', unidadeId),
      where('status', '==', 'aberta')
    );
    let primeiraLeitura = true;
    return onSnapshot(abertas, (snapshot) => {
      setCobrancas(
        snapshot.docs
          .map((d) => ({ id: d.id, ...d.data() } as Cobranca))
          .sort((a, b) => (b.criadaEm?.toMillis() ?? Date.now()) - (a.criadaEm?.toMillis() ?? Date.now()))
      );
      // Saiu da lista de abertas: busca o status final para avisar o caixa.
      if (!primeiraLeitura) {
        snapshot.docChanges()
          .filter((mudanca) => mudanca.type === 'removed')
          .forEach(async (mudanca) => {
            const atual = await getDoc(mudanca.doc.ref);
            if (atual.exists()) onEncerrada({ id: atual.id, ...atual.data() } as Cobranca);
          });
      }
      primeiraLeitura = false;
    });
  }, [unidadeId, onEncerrada]);

  // Atualiza o "há X min" a cada minuto.
  useEffect(() => {
    const intervalo = setInterval(() => setRelogio((n) => n + 1), 60000);
    return () => clearInterval(intervalo);
  }, []);

  const cancelar = async (cobranca: Cobranca) => {
    if (!window.confirm(`Cancelar a cobrança ${cobranca.codigo} de ${formatCurrency(cobranca.valor)}?`)) return;
    const cobrancaId = cobranca.id;
    setCancelando(cobrancaId);
    try {
      await httpsCallable(getFunctions(), 'cancelarCobranca')({ cobrancaId });
    } finally {
      setCancelando(null);
    }
  };

  return (
    <div className="rounded-xl border border-primary/20 p-6 shadow-lg">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-white">Cobranças em aberto</h3>
        <span className="bg-primary text-background text-xs px-2 py-1 rounded-full font-medium">{cobrancas.length}</span>
      </div>

      {cobrancas.length === 0 ? (
        <p className="text-gray-500 text-sm">Nenhuma cobrança aguardando pagamento.</p>
      ) : (
        <ul className="space-y-3">
          {cobrancas.map((cobranca) => (
            <li key={cobranca.id} className="rounded-lg border border-primary/20 p-3">
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-mono font-bold tracking-widest text-primary text-lg">{cobranca.codigo}</span>
                  <span className="text-white font-semibold ml-3">{formatCurrency(cobranca.valor)}</span>
                  {cobranca.numeroComanda && (
                    <span className="text-gray-400 text-sm ml-2">comanda {cobranca.numeroComanda}</span>
                  )}
                </div>
                <span className="text-gray-500 text-xs">{minutosDesde(cobranca)}</span>
              </div>

              {cobranca.ultimaRecusa && (
                <p className="mt-2 text-sm text-red-400">Recusado: {cobranca.ultimaRecusa.motivo}</p>
              )}

              <div className="mt-3 flex gap-2">
                <button
                  onClick={() => onVer(cobranca.id)}
                  className="flex-1 py-1.5 px-3 bg-gray-500 hover:bg-gray-600 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  Ver QR/código
                </button>
                <button
                  onClick={() => compartilharCobranca(cobranca)}
                  className="flex-1 py-1.5 px-3 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  Compartilhar
                </button>
                <button
                  onClick={() => cancelar(cobranca)}
                  disabled={cancelando === cobranca.id}
                  className="flex-1 py-1.5 px-3 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  {cancelando === cobranca.id ? 'Cancelando...' : 'Cancelar'}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CobrancasAbertas;
